import React from "react";
import {IntlShape, injectIntl} from "react-intl";

import {ILicenses} from "@/services/models";
import {ILocalizedText} from "@translate/models";
import {
    CHART_EXPORT_HEIGHT,
    EChartAspectRatio,
    EChartFontSize,
    EChartResolution,
} from "./models";

import InlineRadios from "@toolbox/button-like/InlineRadios";
import T, {intl2Str} from "@translate/T";
import {getChartExportImage} from "./images";

interface IChartExportProps {
    intl: IntlShape;
    chartAspectRatio: EChartAspectRatio;
    chartFontSize: EChartFontSize;
    chartResolution: EChartResolution;
    licenses: ILicenses;

    setChartAspectRatio(chartAspectRatio: EChartAspectRatio): void;
    setChartFontSize(chartFontSize: EChartFontSize): void;
    setChartResolution(chartResolution: EChartResolution): void;
}

class ChartExport extends React.PureComponent<IChartExportProps> {
    public render() {
        const {intl, chartAspectRatio, chartFontSize} = this.props;

        return (
            <div className="form-row mb-2">
                <div className="col-sm-6">
                    {this.renderChartAspectRatio()}
                    {this.renderChartFontSize()}
                    {this.renderChartResolution()}
                </div>

                <div className="col-sm-3 align-self-center">
                    <img
                        alt={intl2Str(intl, "Chart export example")}
                        src={getChartExportImage(
                            chartAspectRatio,
                            chartFontSize,
                        )}
                        height={CHART_EXPORT_HEIGHT}
                    />
                </div>
            </div>
        );
    }

    private renderChartAspectRatio() {
        const {intl, chartAspectRatio, licenses, setChartAspectRatio} =
            this.props;

        let title: ILocalizedText | undefined;
        if (licenses.cent || licenses.spoc) {
            const places: ILocalizedText[] = [];

            if (licenses.cent) {
                places.push((_intl) =>
                    intl2Str(_intl, "Fingerprints & profiles plot"),
                );
            }

            if (licenses.spoc) {
                places.push((_intl) => intl2Str(_intl, "2D density dot plot"));
            }

            title = (_intl) =>
                intl2Str(_intl, "Fixed aspect ratio always for:") +
                " " +
                places.map((place) => place(_intl)).join(", ");
        }

        return (
            <div className="form-row">
                <div className="col-sm-4">
                    <label title={title?.(intl)}>
                        <T>Chart export aspect ratio</T>
                    </label>
                </div>

                <div className="col-sm-8">
                    <InlineRadios<EChartAspectRatio>
                        idSuffix="chart-aspect-ratio"
                        selected={chartAspectRatio}
                        onSelected={setChartAspectRatio}
                        values={[
                            <T key={EChartAspectRatio.Ratio16to9}>16:9</T>,
                            <T key={EChartAspectRatio.Ratio4to3}>4:3</T>,
                            <T key={EChartAspectRatio.Ratio1to1}>1:1</T>,
                            <T key={EChartAspectRatio.Screen}>Screen</T>,
                        ]}
                    />
                </div>
            </div>
        );
    }

    private renderChartFontSize() {
        const {chartResolution, setChartResolution} = this.props;

        return (
            <div className="form-row">
                <div className="col-sm-4">
                    <label>
                        <T>Chart export resolution</T>
                    </label>
                </div>

                <div className="col-sm-8">
                    <InlineRadios<EChartResolution>
                        idSuffix="chart-resolution"
                        selected={chartResolution}
                        onSelected={setChartResolution}
                        values={[
                            <T key={EChartResolution.Low}>Low</T>,
                            <T key={EChartResolution.Medium}>Medium</T>,
                            <T key={EChartResolution.High}>High</T>,
                            <T key={EChartResolution.Screen}>Screen</T>,
                        ]}
                    />
                </div>
            </div>
        );
    }

    private renderChartResolution() {
        const {chartFontSize, setChartFontSize} = this.props;

        return (
            <div className="form-row">
                <div className="col-sm-4">
                    <label>
                        <T>Chart export font size</T>
                    </label>
                </div>

                <div className="col-sm-8">
                    <InlineRadios<EChartFontSize>
                        idSuffix="chart-font-size"
                        selected={chartFontSize}
                        onSelected={setChartFontSize}
                        values={[
                            <T key={EChartFontSize.Res100}>100%</T>,
                            <T key={EChartFontSize.Res125}>125%</T>,
                            <T key={EChartFontSize.Res150}>150%</T>,
                            <T key={EChartFontSize.Res175}>175%</T>,
                            <T key={EChartFontSize.Res200}>200%</T>,
                        ]}
                    />
                </div>
            </div>
        );
    }
}

export default injectIntl(ChartExport);
